import React from "react"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material"
import AddMarksMap from "./AddMarksMap"
import { useMutation, gql } from "@apollo/client"

const ADD_MARKS = gql`
  mutation addMarks($marks: [marker_points_insert_input!]!) {
    insert_marker_points(objects: $marks) {
      returning {
        id
        area_id
      }
    }
  }
`

export default function AddMarksIndex({
  open,
  setAddingMarks,
  activeAreaId,
  initialCenter,
  marksGeoJSON,
  farmAreaGeoJSON,
}) {
  // Initialize marks from activeAreaId when dialog opens
  const [marks, setMarks] = React.useState({
    type: "FeatureCollection",
    features: [],
  })
  const [markName, setMarkName] = React.useState("")
  const [error, setError] = React.useState("")
  const [isSaving, setIsSaving] = React.useState(false)
  const [addMarks] = useMutation(ADD_MARKS, {
    onError: (err) => {
      setError(err.message || "Failed to save marks")
      setIsSaving(false)
    },
    onCompleted: () => {
      setIsSaving(false)
      setAddingMarks(false)
      setMarkName("")
    },
  })

  // Update marks when activeAreaId changes
  React.useEffect(() => {
    if (activeAreaId && marksGeoJSON?.features) {
      const activeAreaMarks = {
        type: "FeatureCollection",
        features: marksGeoJSON.features.filter(
          (m) => m?.properties?.area_id === activeAreaId
        ),
      }
      setMarks(activeAreaMarks)
    } else {
      setMarks({
        type: "FeatureCollection",
        features: [],
      })
    }
  }, [activeAreaId, marksGeoJSON])

  const handleSaveMarks = () => {
    if (isSaving) return

    // Get unsaved marks
    const unsavedMarks = marks.features.filter((m) => !m.id)

    // Deduplicate by coordinates
    const seenCoordinates = new Set()
    const uniqueMarks = unsavedMarks.filter((mark) => {
      if (mark.geometry?.coordinates?.length !== 2) return false

      const coordKey = `${mark.geometry.coordinates[0]},${mark.geometry.coordinates[1]}`
      if (seenCoordinates.has(coordKey)) return false

      seenCoordinates.add(coordKey)
      return true
    })

    if (uniqueMarks.length === 0) {
      setError("No valid marks to save")
      return
    }

    setIsSaving(true)
    setError("")

    addMarks({
      variables: {
        marks: uniqueMarks.map((mark) => ({
          area_id: activeAreaId,
          type: "marker_point",
          name: markName || "Unnamed Mark",
          geo_json: {
            type: "Point",
            coordinates: mark.geometry.coordinates,
          },
          metadata: {},
          archived_at: null,
        })),
      },
    })
  }

  const activeAreaGeoJson = {
    ...farmAreaGeoJSON,
    features: [
      ...farmAreaGeoJSON.features.filter((area) => area.id === activeAreaId),
    ],
  }

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogTitle>Add marks to a farm area</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Mark Name"
          value={markName}
          onChange={(e) => setMarkName(e.target.value)}
          fullWidth
          margin="normal"
          placeholder="e.g., Sampling Point 1, Buoy Location, etc."
        />
        <AddMarksMap
          initialCenter={initialCenter}
          marks={marks}
          setMarks={setMarks}
          marksGeoJSON={marksGeoJSON}
          activeAreaGeoJson={activeAreaGeoJson}
        />
        Adding Marks to area: {activeAreaId}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setAddingMarks(false)
            setMarkName("")
            setError("")
          }}
        >
          Discard Changes
        </Button>

        <Button onClick={handleSaveMarks} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
